import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import Breadcrumb from '../components/common/Breadcrumb';

const PAGE_SIZE = 30;

const VERDICT_FILTERS = [
  { key: 'all', i18nKey: 'history.allVerdicts' },
  { key: 'scam', i18nKey: 'result.verdictScam', className: 'badge-danger' },
  { key: 'suspicious', i18nKey: 'result.verdictSuspicious', className: 'badge-suspicious' },
  { key: 'caution', i18nKey: 'result.verdictCaution', className: 'badge-caution' },
  { key: 'legitimate', i18nKey: 'result.verdictLegitimate', className: 'badge-safe' },
];

const CHANNEL_OPTIONS = [
  { key: 'all', i18nKey: 'history.allChannels', icon: '🔍' },
  { key: 'email', label: 'Email', icon: '📧' },
  { key: 'sms', label: 'SMS', icon: '💬' },
  { key: 'whatsapp', label: 'WhatsApp', icon: '📱' },
  { key: 'telegram', label: 'Telegram', icon: '✈️' },
  { key: 'messenger', label: 'Messenger', icon: '💭' },
  { key: 'instagram', label: 'Instagram', icon: '📷' },
  { key: 'leboncoin', label: 'Leboncoin', icon: '🏷️' },
  { key: 'marketplace', label: 'Marketplace', icon: '🛒' },
  { key: 'vinted', label: 'Vinted', icon: '👗' },
  { key: 'phone', i18nKey: 'channels.phone', icon: '📞' },
  { key: 'website', i18nKey: 'channels.website', icon: '🌐' },
  { key: 'other', i18nKey: 'common.other', icon: '❓' },
];

const SORT_OPTIONS = [
  { key: 'newest', i18nKey: 'history.sortRecent' },
  { key: 'oldest', i18nKey: 'history.sortOldest' },
  { key: 'score_desc', i18nKey: 'history.sortHighScore' },
  { key: 'score_asc', i18nKey: 'history.sortLowScore' },
];

export default function HistoryPage() {
  const { t } = useTranslation();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [verdictFilter, setVerdictFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const fetchPage = useCallback((currentOffset, append = false) => {
    const setter = append ? setLoadingMore : setLoading;
    setter(true);
    return api.getHistory({ limit: PAGE_SIZE, offset: currentOffset })
      .then(res => {
        const results = res.data?.results || res.data || [];
        if (append) {
          setAnalyses(prev => [...prev, ...results]);
        } else {
          setAnalyses(results);
        }
        setHasMore(results.length >= PAGE_SIZE);
        setOffset(currentOffset + results.length);
      })
      .catch(() => {})
      .finally(() => setter(false));
  }, []);

  useEffect(() => {
    fetchPage(0);
  }, [fetchPage]);

  const handleLoadMore = () => {
    fetchPage(offset, true);
  };

  const getVerdictStyle = (verdict) => {
    const map = {
      legitimate: 'badge-safe',
      caution: 'badge-caution',
      suspicious: 'badge-suspicious',
      scam: 'badge-danger',
    };
    return map[verdict] || 'badge-safe';
  };

  const channelIcons = {
    email: '📧', sms: '💬', whatsapp: '📱', telegram: '✈️',
    messenger: '💭', instagram: '📷', leboncoin: '🏷️',
    marketplace: '🛒', vinted: '👗', phone: '📞', website: '🌐', other: '❓',
  };

  // Client-side filtering, sorting, searching via useMemo
  const filteredAnalyses = useMemo(() => {
    let results = [...analyses];

    // Search filter
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      results = results.filter(a =>
        (a.sender_email && a.sender_email.toLowerCase().includes(q)) ||
        (a.sender_domain && a.sender_domain.toLowerCase().includes(q)) ||
        (a.subject && a.subject.toLowerCase().includes(q))
      );
    }

    // Verdict filter
    if (verdictFilter !== 'all') {
      results = results.filter(a => a.verdict === verdictFilter);
    }

    // Channel filter
    if (channelFilter !== 'all') {
      results = results.filter(a => a.channel === channelFilter);
    }

    // Sort
    results.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'score_desc':
          return (b.risk_score || 0) - (a.risk_score || 0);
        case 'score_asc':
          return (a.risk_score || 0) - (b.risk_score || 0);
        case 'newest':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    return results;
  }, [analyses, search, verdictFilter, channelFilter, sortBy]);

  const isFiltering = search.trim() || verdictFilter !== 'all' || channelFilter !== 'all';

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <Breadcrumb items={[{ label: t('breadcrumb.home'), to: '/' }, { label: t('breadcrumb.history') }]} />
      <h1 className="text-3xl font-bold text-white mb-2 mt-4">{t('history.title')}</h1>
      <p className="text-gray-400 mb-6">{t('history.subtitle')}</p>

      {/* Sticky search/filter bar */}
      {!loading && analyses.length > 0 && (
        <div className="sticky top-16 z-20 bg-dark-bg/80 backdrop-blur-md pb-4 -mx-4 px-4 pt-2">
          {/* Search input */}
          <div className="relative mb-3">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('history.search')}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-dark-surface border border-dark-border text-white text-sm placeholder-gray-500 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Filters row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Verdict filter buttons */}
            <div className="flex flex-wrap gap-1.5">
              {VERDICT_FILTERS.map(v => (
                <button
                  key={v.key}
                  onClick={() => setVerdictFilter(v.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    verdictFilter === v.key
                      ? 'bg-primary/20 text-primary border border-primary/40'
                      : 'bg-dark-surface text-gray-400 border border-dark-border hover:border-gray-600 hover:text-gray-300'
                  }`}
                >
                  {v.i18nKey ? t(v.i18nKey) : v.label}
                </button>
              ))}
            </div>

            <div className="h-5 w-px bg-dark-border hidden sm:block" />

            {/* Channel dropdown */}
            <select
              value={channelFilter}
              onChange={e => setChannelFilter(e.target.value)}
              className="px-3 py-1.5 rounded-md text-xs font-medium bg-dark-surface text-gray-400 border border-dark-border hover:border-gray-600 focus:outline-none focus:border-primary/50 cursor-pointer transition-colors appearance-none pr-7 bg-no-repeat bg-[length:12px] bg-[right_8px_center]"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z'/%3E%3C/svg%3E\")" }}
            >
              {CHANNEL_OPTIONS.map(ch => (
                <option key={ch.key} value={ch.key}>
                  {ch.icon} {ch.i18nKey ? t(ch.i18nKey) : ch.label}
                </option>
              ))}
            </select>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Sort dropdown */}
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="px-3 py-1.5 rounded-md text-xs font-medium bg-dark-surface text-gray-400 border border-dark-border hover:border-gray-600 focus:outline-none focus:border-primary/50 cursor-pointer transition-colors appearance-none pr-7 bg-no-repeat bg-[length:12px] bg-[right_8px_center]"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z'/%3E%3C/svg%3E\")" }}
            >
              {SORT_OPTIONS.map(s => (
                <option key={s.key} value={s.key}>{s.i18nKey ? t(s.i18nKey) : s.label}</option>
              ))}
            </select>
          </div>

          {/* Result count */}
          <div className="mt-3 text-xs text-gray-500">
            {t('history.results', { count: filteredAnalyses.length })}
            {isFiltering ? ` ${t('history.resultsFiltered', { total: analyses.length })}` : ''}
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mb-4" />
          <p className="text-gray-500">{t('common.loading')}</p>
        </div>
      ) : analyses.length === 0 ? (
        /* Empty state with CTA */
        <div className="text-center py-20">
          <svg
            className="w-16 h-16 mx-auto mb-6 text-gray-600"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
          </svg>
          <h2 className="text-xl font-semibold text-white mb-2">{t('history.emptyTitle')}</h2>
          <p className="text-gray-400 mb-6">{t('history.emptyDesc')}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            {t('history.emptyAction')}
          </Link>
        </div>
      ) : filteredAnalyses.length === 0 ? (
        /* No results for current filters */
        <div className="text-center py-16">
          <svg
            className="w-12 h-12 mx-auto mb-4 text-gray-600"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <p className="text-gray-400 mb-2">{t('history.noResults')}</p>
          <button
            onClick={() => { setSearch(''); setVerdictFilter('all'); setChannelFilter('all'); }}
            className="mt-4 text-primary hover:text-primary/80 text-sm font-medium transition-colors"
          >
            {t('history.resetFilters')}
          </button>
        </div>
      ) : (
        <>
          {/* Analysis cards */}
          <div className="space-y-3">
            {filteredAnalyses.map((a, i) => (
              <Link
                key={a.id || i}
                to={`/result/${encodeURIComponent(a.sender_email || a.sender_phone || a.sender_pseudo || a.id)}`}
                className="glass-card-hover p-4 flex items-center justify-between block"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {a.channel && a.channel !== 'email' && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-dark-surface border border-dark-border text-gray-400" title={a.channel}>
                        {channelIcons[a.channel] || '📧'}
                      </span>
                    )}
                    <span className="text-white font-mono text-sm">{a.sender_email || a.sender_domain}</span>
                  </div>
                  <div className="text-gray-500 text-xs truncate">{a.subject}</div>
                  <div className="text-gray-600 text-xs mt-1">{new Date(a.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <span className="font-mono text-sm text-gray-300">{a.risk_score}/100</span>
                  <span className={getVerdictStyle(a.verdict)}>{a.verdict}</span>
                </div>
              </Link>
            ))}
          </div>

          {/* Load more button */}
          {hasMore && !isFiltering && (
            <div className="text-center mt-8">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="px-6 py-2.5 rounded-lg bg-dark-surface border border-dark-border text-gray-300 hover:text-white hover:border-gray-500 font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-gray-500 border-t-gray-300 rounded-full animate-spin" />
                    {t('history.loading')}
                  </span>
                ) : (
                  t('history.loadMore')
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
